import { inject } from '@tokamakjs/injection';
import { HookService } from '@tokamakjs/react';
import jwtDecode from 'jwt-decode';
import { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';
import join from 'url-join';

import { AppContext, Invitation, LocaleCode, UserFromToken } from '~/types';
import { APP_CONTEXT, getJwt, getLatestJwt, refreshJwt } from '~/utils';

export interface SignUpResult {
  jwt: string;
  refresh_token: string;
}

export enum SignupError {
  TAKEN = 'taken',
  OTHER = 'other',
}

export enum ConfirmUserResult {
  GENERIC_VALIDATION_ERROR = 'GENERIC_VALIDATION_ERROR',
  EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED',
  SUCCESS = 'SUCCESS',
}
export type UserSignupFormValue = {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  locale: LocaleCode;
};

export enum UserLoginResult {
  SUCCESS = 'SUCCESS',
  WRONG_CREDENTIALS = 'WRONG_CREDENTIALS',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum ChangePasswordError {
  LINK_NOT_RECOGNIZED = 'LINK_NOT_RECOGNIZED',
  GENERIC = 'GENERIC',
}

@HookService()
export class AuthApi {
  constructor(@inject(APP_CONTEXT) private readonly _context: AppContext) {}

  async refreshJwt() {
    const authHost = this._context.env.AUTH_HOST;

    await refreshJwt(authHost, this._context.targetDomain);
  }

  async signUp(data: UserSignupFormValue & { invitation_token: undefined | string }): Promise<
    | {
        error: SignupError;
        data?: undefined;
      }
    | {
        error?: undefined;
        data: SignUpResult;
      }
  > {
    const authHost = this._context.env.AUTH_HOST;

    if (!authHost) {
      throw new Error();
    }

    const res = await fetch(join(authHost, 'v1/register'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Target-Domain': this._context.targetDomain,
      },
      body: JSON.stringify({
        user: data,
      }),
    });

    if (!res.ok) {
      const body = await res.json();

      if (body.error?.[0]?.email?.[0]?.error === SignupError.TAKEN) {
        return {
          error: SignupError.TAKEN,
        };
      } else {
        return {
          error: SignupError.OTHER,
        };
      }
    } else {
      const parsed: SignUpResult = await res.json();

      return {
        data: parsed,
      };
    }
  }

  async confirmUser(token: string): Promise<
    | {
        error: ConfirmUserResult.GENERIC_VALIDATION_ERROR;
      }
    | {
        error?: undefined;
        data: {
          user: UserFromToken;
          jwt: string;
        };
      }
  > {
    const authHost = this._context.env.AUTH_HOST;

    const res = await fetch(join(authHost, '/v1/confirm-user'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${await getLatestJwt(authHost, this._context.targetDomain)}`,
        'Content-Type': 'application/json',
        'Target-Domain': this._context.targetDomain,
      },
      body: JSON.stringify({
        token,
        target_domain: this._context.targetDomain,
      }),
    });

    if (!res.ok) {
      return {
        error: ConfirmUserResult.GENERIC_VALIDATION_ERROR,
      };
    }

    const { jwt } = await res.json();
    const user = jwtDecode<UserFromToken>(jwt);

    return {
      data: {
        user,
        jwt,
      },
    };
  }

  async getUserFromMostRecentToken(): Promise<
    | {
        error: true;
        data?: undefined;
      }
    | {
        error?: undefined;
        data: UserFromToken;
      }
  > {
    const userRes = await this.getUserFromMostRecentTokenIfLoggedIn();

    if (userRes.error != null) {
      return userRes;
    }

    if (userRes.data == null) {
      return {
        error: true,
      };
    } else {
      return {
        data: userRes.data,
      };
    }
  }

  async getUserFromMostRecentTokenIfLoggedIn(): Promise<
    | {
        error: true;
        data?: undefined;
      }
    | {
        error?: undefined;
        data: UserFromToken | undefined;
      }
  > {
    const token = await getLatestJwt(this._context.env.AUTH_HOST, this._context.targetDomain);

    if (!token) {
      return {
        data: undefined,
      };
    }

    const decodedUser = jwtDecode<UserFromToken | undefined>(token);

    if (!decodedUser) {
      return {
        error: true,
      };
    } else {
      return {
        data: decodedUser,
      };
    }
  }

  getUserFromLocalToken(): UserFromToken | undefined {
    const token = getJwt();

    if (!token) {
      return undefined;
    }

    const decodedUser = jwtDecode<UserFromToken | undefined>(token);

    if (!decodedUser) {
      return undefined;
    } else {
      return decodedUser;
    }
  }

  async createCompany(values: { name: string }) {
    const authHost = this._context.env.AUTH_HOST;
    const res = await fetch(join(authHost, '/v1/organisations'), {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getLatestJwt(authHost, this._context.targetDomain)}`,
        'Target-Domain': this._context.targetDomain,
      },
      body: JSON.stringify({
        target_domain: this._context.targetDomain,
        organisation_name: values.name,
      }),
    });

    if (res.status === 500) {
      return {
        error: true,
      } as const;
    } else {
      const { jwt } = (await res.json()) as { jwt: string };

      return {
        data: { jwt },
      } as const;
    }
  }

  async joinCompany(invitationToken: string): Promise<
    | {
        error: true;
        data?: undefined;
      }
    | {
        error?: undefined;
        data: {
          jwt: string;
          refresh_token: string;
        };
      }
  > {
    const authHost = this._context.env.AUTH_HOST;

    const res = await fetch(join(authHost, '/v1/join-company'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${await getLatestJwt(authHost, this._context.targetDomain)}`,
        'Content-Type': 'application/json',
        'Target-Domain': this._context.targetDomain,
      },
      body: JSON.stringify({
        target_domain: this._context.targetDomain,
        invitation_token: invitationToken,
      }),
    });

    if (res.ok) {
      const data = (await res.json()) as { jwt: string; refresh_token: string };

      return { data };
    } else {
      return { error: true };
    }
  }

  async login(values: { email: string; password: string }): Promise<
    | {
        result: UserLoginResult.WRONG_CREDENTIALS | UserLoginResult.GENERIC_ERROR;
      }
    | {
        result: UserLoginResult.SUCCESS;
        data: {
          jwt: string;
          refresh_token: string;
        };
      }
  > {
    const res = await fetch(join(this._context.env.AUTH_HOST, '/v1/login'), {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'Target-Domain': this._context.targetDomain,
      },
      body: JSON.stringify(values),
    });

    if (res.status === 500) {
      return { result: UserLoginResult.GENERIC_ERROR };
    } else if (!res.ok) {
      return { result: UserLoginResult.WRONG_CREDENTIALS };
    } else {
      const parsed = await res.json();

      return {
        result: UserLoginResult.SUCCESS,
        data: parsed,
      };
    }
  }

  async fetchInvitation(code: string): Promise<
    | {
        error: true;
        data?: undefined;
      }
    | {
        error?: undefined;
        data: Invitation;
      }
  > {
    const authHost = this._context.env.AUTH_HOST;

    const res = await fetch(join(authHost, '/v1/invitations', code), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${await getLatestJwt(authHost, this._context.targetDomain)}`,
      },
    });

    if (!res.ok) {
      return {
        error: true,
      };
    }

    const { invitation: invitationFromServer } = await res.json();

    const invitation: Invitation = {
      id: invitationFromServer.id,
      email: invitationFromServer.email,
      firstName: invitationFromServer.first_name,
      lastName: invitationFromServer.last_name,
      organisationName: invitationFromServer.organisation_name,
      organisationId: invitationFromServer.organisation_id,
      token: invitationFromServer.token,
      status: invitationFromServer.status,
    };

    return {
      data: invitation,
    };
  }

  async handleSuccessfulGoogleLogin(
    googleResponse: GoogleLoginResponse | GoogleLoginResponseOffline,
  ): Promise<
    | {
        error: 'generic' | 'invalid_user';
      }
    | {
        error?: undefined;
        data: {
          jwt: string;
          refresh_token: string;
        };
      }
  > {
    const authHost = this._context.env.AUTH_HOST;

    const res = await fetch(
      join(
        authHost,
        `/v1/google_oauth2/callback?code=${googleResponse.code}&flow=login&target_domain=${this._context.targetDomain}`,
      ),
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!res.ok) {
      if (res.status < 500) {
        const data = (await res.json()) as { error: string[] };

        const error = data.error[0];

        if (error === 'invalid_user') {
          return { error: 'invalid_user' };
        } else {
          return { error: 'generic' };
        }
      } else {
        return { error: 'generic' };
      }
    } else {
      const parsed = (await res.json()) as { jwt: string; refresh_token: string };

      return {
        data: parsed,
      };
    }
  }

  async handleSuccessfulMicrosoftLogin(code: string): Promise<
    | {
        error: 'generic' | 'invalid_user';
      }
    | {
        error?: undefined;
        data: {
          jwt: string;
          refresh_token: string;
        };
      }
  > {
    const authHost = this._context.env.AUTH_HOST;

    const res = await fetch(
      join(
        authHost,
        `/v1/microsoft_oauth2/callback?flow=login${
          code != null ? `&code=${code}?` : ''
        }&target_domain=${this._context.targetDomain}`,
      ),
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!res.ok) {
      if (res.status < 500) {
        const data = (await res.json()) as { error: string[] };

        const error = data.error[0];

        if (error === 'invalid_user') {
          return { error: 'invalid_user' };
        } else {
          return { error: 'generic' };
        }
      } else {
        return { error: 'generic' };
      }
    } else {
      const parsed = (await res.json()) as { jwt: string; refresh_token: string };

      return {
        data: parsed,
      };
    }
  }

  async handleSuccessfulGoogleSignup(
    googleResponse: GoogleLoginResponse | GoogleLoginResponseOffline,
    invitationToken: string | undefined,
  ): Promise<
    | {
        error: 'generic' | 'user_already_exists';
      }
    | {
        error?: undefined;
        data: {
          jwt: string;
          refresh_token: string;
        };
      }
  > {
    const authHost = this._context.env.AUTH_HOST;

    const res = await fetch(
      join(
        authHost,
        invitationToken
          ? `/v1/google_oauth2/callback?code=${googleResponse.code}&invitation_token=${invitationToken}&flow=sign-up&target_domain=${this._context.targetDomain}`
          : `/v1/google_oauth2/callback?code=${googleResponse.code}&flow=sign-up&target_domain=${this._context.targetDomain}`,
      ),
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!res.ok) {
      if (res.status < 500) {
        const data = (await res.json()) as { error: string[] };

        const error = data.error[0];

        if (error === 'user_already_exists') {
          return { error: 'user_already_exists' };
        } else {
          return { error: 'generic' };
        }
      } else {
        return { error: 'generic' };
      }
    } else {
      const parsed = await res.json();

      return {
        data: parsed,
      };
    }
  }

  async handleSuccessfulMicrosoftSignup(
    code: string,
    invitationToken: string | undefined,
  ): Promise<
    | {
        error: 'generic' | 'user_already_exists';
      }
    | {
        error?: undefined;
        data: {
          jwt: string;
          refresh_token: string;
        };
      }
  > {
    const authHost = this._context.env.AUTH_HOST;

    const res = await fetch(
      join(
        authHost,
        invitationToken
          ? `/v1/microsoft_oauth2/callback?code=${code}&invitation_token=${invitationToken}&flow=sign-up&target_domain=${this._context.targetDomain}`
          : `/v1/microsoft_oauth2/callback?code=${code}&flow=sign-up&target_domain=${this._context.targetDomain}`,
      ),
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!res.ok) {
      if (res.status < 500) {
        const data = (await res.json()) as { error: string[] };

        const error = data.error[0];

        if (error === 'user_already_exists') {
          return { error: 'user_already_exists' };
        } else {
          return { error: 'generic' };
        }
      } else {
        return { error: 'generic' };
      }
    } else {
      const parsed = await res.json();

      return {
        data: parsed,
      };
    }
  }

  async sendPasswordResetRequest(values: { email: string }): Promise<
    | {
        error: true;
      }
    | {
        error?: undefined;
      }
  > {
    const authHost = this._context.env.AUTH_HOST;

    const res = await fetch(join(authHost, '/v1/forgotten-password'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Target-Domain': this._context.targetDomain,
      },
      body: JSON.stringify({
        email: values.email.toLowerCase(),
      }),
    });

    if (res.ok) {
      return {
        error: undefined,
      };
    } else {
      return {
        error: true,
      };
    }
  }

  async changePassword(values: { password: string; code: string }): Promise<
    | {
        error: ChangePasswordError;
      }
    | {
        error: undefined;
      }
  > {
    const authHost = this._context.env.AUTH_HOST;

    const res = await fetch(join(authHost, '/v1/forgotten-password'), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Target-Domain': this._context.targetDomain,
      },
      body: JSON.stringify({
        reset_password_token: values.code,
        password: values.password,
      }),
    });

    if (res.ok) {
      return {
        error: undefined,
      };
    } else {
      const body = await res.json();
      if (body.error === 'not_authorized') {
        return {
          error: ChangePasswordError.LINK_NOT_RECOGNIZED,
        };
      } else {
        return {
          error: ChangePasswordError.GENERIC,
        };
      }
    }
  }
}
